/**
 * Centralized Configuration Management for WithMilo
 * Handles environment variables, model configuration, and validation
 */

// Environment Configuration
export const ENV_CONFIG = {
  // Supabase Configuration
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  
  // OpenAI Configuration  
  OPENAI_API_KEY: import.meta.env.VITE_OPENAI_API_KEY,
  
  // Model Configuration - Centralized
  CLASSIFY_MODEL: import.meta.env.VITE_CLASSIFY_MODEL || 'gpt-4o-mini',
  CLASSIFY_FALLBACK_MODEL: import.meta.env.VITE_CLASSIFY_FALLBACK_MODEL || 'gpt-4o',
  EXTRACT_TEXT_MODEL: import.meta.env.VITE_EXTRACT_TEXT_MODEL || 'o3',
  EXTRACT_VISION_MODEL: import.meta.env.VITE_EXTRACT_VISION_MODEL || 'o3',
  
  // Processing Configuration
  MAX_FILE_SIZE: parseInt(import.meta.env.VITE_MAX_FILE_SIZE || '15728640'), // 15MB default
  PROCESSING_TIMEOUT: parseInt(import.meta.env.VITE_PROCESSING_TIMEOUT || '120000'), // 2 minutes
  
  // Admin Configuration (hidden from users)
  ADMIN_MODE: import.meta.env.VITE_ADMIN_MODE === 'true',
  COST_TRACKING_ENABLED: import.meta.env.VITE_COST_TRACKING_ENABLED === 'true',
  
  // Feature Flags
  EDGE_FUNCTIONS_ENABLED: import.meta.env.VITE_EDGE_FUNCTIONS_ENABLED !== 'false', // Default true
  CLIENT_FALLBACK_ENABLED: import.meta.env.VITE_CLIENT_FALLBACK_ENABLED !== 'false', // Default true
} as const;

// Model Configuration Object
export const MODELS = {
  classify: {
    primary: ENV_CONFIG.CLASSIFY_MODEL,
    fallback: ENV_CONFIG.CLASSIFY_FALLBACK_MODEL
  },
  extract: {
    text: ENV_CONFIG.EXTRACT_TEXT_MODEL,
    vision: ENV_CONFIG.EXTRACT_VISION_MODEL
  }
} as const;

// Required Environment Variables
const REQUIRED_ENV_VARS = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY'
] as const;

// Optional but recommended environment variables
const RECOMMENDED_ENV_VARS = [
  'VITE_OPENAI_API_KEY'
] as const;

// Validation Results Interface
interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  config: typeof ENV_CONFIG;
}

/**
 * Validates all required environment variables are present
 * Called at application startup
 */
export function validateEnvironment(): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  REQUIRED_ENV_VARS.forEach(envVar => {
    const value = import.meta.env[envVar];
    if (!value || value.trim() === '') {
      errors.push(`Missing required environment variable: ${envVar}`);
    }
  });

  // Check recommended variables
  RECOMMENDED_ENV_VARS.forEach(envVar => {
    const value = import.meta.env[envVar];
    if (!value || value.trim() === '') {
      warnings.push(`Missing recommended environment variable: ${envVar} - some features may not work`);
    }
  });

  // Validate Supabase URL format
  if (ENV_CONFIG.SUPABASE_URL && !ENV_CONFIG.SUPABASE_URL.includes('supabase.co')) {
    warnings.push('VITE_SUPABASE_URL may not be a valid Supabase URL');
  }

  // Validate model configurations
  const validModels = ['gpt-4o-mini', 'gpt-4o', 'o3', 'o3-mini'];
  if (!validModels.includes(ENV_CONFIG.CLASSIFY_MODEL)) {
    warnings.push(`Unknown classification model: ${ENV_CONFIG.CLASSIFY_MODEL}`);
  }
  if (!validModels.includes(ENV_CONFIG.EXTRACT_TEXT_MODEL)) {
    warnings.push(`Unknown text extraction model: ${ENV_CONFIG.EXTRACT_TEXT_MODEL}`);
  }

  const isValid = errors.length === 0;

  // Log validation results (admin only)
  if (ENV_CONFIG.ADMIN_MODE) {
    console.group('🔧 Environment Validation');
    console.log('Models configured:', MODELS);
    console.log('Features enabled:', {
      edgeFunctions: ENV_CONFIG.EDGE_FUNCTIONS_ENABLED,
      clientFallback: ENV_CONFIG.CLIENT_FALLBACK_ENABLED,
      costTracking: ENV_CONFIG.COST_TRACKING_ENABLED
    });
    if (errors.length > 0) {
      console.error('❌ Validation errors:', errors);
    }
    if (warnings.length > 0) {
      console.warn('⚠️ Validation warnings:', warnings);
    }
    if (isValid) {
      console.log('✅ Environment validation passed');
    }
    console.groupEnd();
  }

  return {
    isValid,
    errors,
    warnings,
    config: ENV_CONFIG
  };
}

/**
 * Gets the current model configuration
 * Used by AI processing modules
 */
export function getModelConfig() {
  return MODELS;
}

/**
 * Admin-only configuration access
 * Returns detailed config info for administrative purposes
 */
export function getAdminConfig() {
  if (!ENV_CONFIG.ADMIN_MODE) {
    throw new Error('Admin mode not enabled');
  }

  return {
    environment: ENV_CONFIG,
    models: MODELS,
    validation: validateEnvironment(),
    features: {
      edgeFunctions: ENV_CONFIG.EDGE_FUNCTIONS_ENABLED,
      clientFallback: ENV_CONFIG.CLIENT_FALLBACK_ENABLED,
      costTracking: ENV_CONFIG.COST_TRACKING_ENABLED
    }
  };
}

/**
 * Check if admin features should be shown
 */
export function isAdminMode(): boolean {
  return ENV_CONFIG.ADMIN_MODE;
}

/**
 * Check if cost tracking is enabled (admin only)
 */
export function isCostTrackingEnabled(): boolean {
  return ENV_CONFIG.ADMIN_MODE && ENV_CONFIG.COST_TRACKING_ENABLED;
}

// Export commonly used values
export const { SUPABASE_URL, SUPABASE_ANON_KEY, MAX_FILE_SIZE, PROCESSING_TIMEOUT } = ENV_CONFIG;